import { createContext, useEffect, useState, useContext, useCallback, useRef } from "react";
import { useApp } from "../../hooks/useApp";
import { WebSocketContext } from '../../contexts/WebSocketContext';

import {
    WhatsAppGetMessageListRequest,
    WhatsAppSendFile,
    WhatsAppSendMessageRequest,
    notificationNewMessage,
} from "../../services/whatsapp/WhatsAppService";

import {
    getMessageTemplateRequest
} from "../../services/templates/MessageTemplatesService";

import {
    ClientUpdatedRequest,
    ClientDelete,
    ClientGetById,
    ClientToggleBotPause
} from "../../services/client/ClientService";

import {
    logConversationViewed,
    logMessageSent,
    logStatusChange,
    logTagChange,
    logClientInfoUpdate
} from "../../services/clientActivityLog/ClientActivityLogService";

import {
    DeleteAiConversationHistory
} from "../../services/autoreply/AutoReplyService";

export const ChatNuevoContext = createContext();

export const ChatNuevoProvider = ({ children }) => {
    // Intentar recuperar el cliente del localStorage al iniciar (solo una vez)
    const [client, setClient] = useState(() => {
        try {
            const savedClient = localStorage.getItem('selectedClient');
            if (savedClient) {
                const clientData = JSON.parse(savedClient);
                return { current: clientData };
            }
            return { current: null };
        } catch (error) {
            console.error('Error loading client from localStorage:', error);
            localStorage.removeItem('selectedClient');
            return { current: null };
        }
    });
    const [chatMessages, setChatMessages] = useState([]);
    const [responseMessageList, setResponseMessageList] = useState([]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [quickResponses, setQuickResponses] = useState([]);
    const [showQuickResponses, setShowQuickResponses] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [isNewMessage, setIsNewMessage] = useState(false);
    const [idMessageOld, setIdMessageOld] = useState(0);
    const [idMessageNew, setIdMessageNew] = useState(0);
    const [includeUserName, setIncludeUserName] = useState(false);

    const [showEmojis, setShowEmojis] = useState(false);
    const [typingClient, setTypingClient] = useState(null);
    const [botPaused, setBotPaused] = useState(false);
    const { authInfo, setErrorMsg, setMessageUser } = useApp();
    const { notificationsWS, sendNotificateWS, sendTypingWS, typingClientWS } = useContext(WebSocketContext);

    // Ref para evitar múltiples inicializaciones
    const isInitialized = useRef(false);
    const clientIdRef = useRef(null);
    // Ref para evitar registrar "vio conversación" múltiples veces para el mismo cliente
    const lastViewedClientId = useRef(null);

    // Efecto para procesar los mensajes recibidos
    useEffect(() => {
        if (responseMessageList.length > 0) {
            let secondRemoved = responseMessageList;

            // Actualizar estados de mensajes existentes
            responseMessageList.forEach((messagedb) => {
                chatMessages.forEach((messagelocal) => {
                    if (messagedb.messageSid === messagelocal.id) {
                        if (messagedb.smsStatus !== messagelocal.smsStatus) {
                            updateFieldChanged(messagelocal.id, messagedb.smsStatus);
                        }
                        secondRemoved = secondRemoved.filter(
                            (objfilter) => objfilter.messageSid !== messagedb.messageSid
                        );
                    }
                });
            });

            // Formatear y agregar nuevos mensajes
            const newMessages = secondRemoved.map(msg => ({
                id: msg.messageSid,
                message: msg.body,
                date: msg.dateFormat,
                messageIncoming: msg.messageIncoming,
                smsStatus: msg.smsStatus || 'sent',
                whatsappMedia: msg.whatsappMedia
            }));

            // Agregar nuevos mensajes al inicio
            if (newMessages.length > 0) {
                setChatMessages(prev => [...newMessages, ...prev]);
            }
        }
    }, [responseMessageList, chatMessages]);

    const loadMessages = useCallback(async (clientId, isInitialLoad = true) => {
        try {
            // Evitar cargas duplicadas para el mismo cliente
            if (isInitialLoad && (loading || (isInitialized.current && clientIdRef.current === clientId))) {
                console.log('Ya hay una carga en progreso o ya se inicializó para este cliente, evitando duplicado');
                return 0;
            }

            if (isInitialLoad) {
                isInitialized.current = true;
                clientIdRef.current = clientId;
                setLoading(true);
                setIsNewMessage(true); // Marcar como nuevos mensajes en la carga inicial
            } else {
                if (!hasMoreMessages || isLoadingMore) return;
                setIsLoadingMore(true);
                setIsNewMessage(false); // No son mensajes nuevos cuando cargamos hacia arriba
            }

            const list = await WhatsAppGetMessageListRequest(clientId, 0, 20, idMessageOld);

            if (isInitialLoad) {
                sendNotificateWS([{ type: 'remove', clientId }]);
                // Clear unread badge immediately (don't wait for backend re-poll)
                setMessageUser(prev => prev.filter(item => item.client?.id !== clientId));

                // Registrar que el usuario vio la conversación (solo una vez por cliente)
                if (authInfo?.id && authInfo?.name && lastViewedClientId.current !== clientId) {
                    lastViewedClientId.current = clientId;
                    logConversationViewed(clientId, authInfo.id, authInfo.name)
                        .catch(err => console.warn('Error al registrar vista de conversación:', err));
                }
            }

            const listFinal = list.content.reverse();

            // Actualizar hasMoreMessages basado en si recibimos menos mensajes de los solicitados
            if (listFinal.length < 20) {
                setHasMoreMessages(false);
            }

            if (listFinal.length > 0 && listFinal[0] && listFinal[0].id) {
                setIdMessageOld(listFinal[0].id);
            }
            if (list.content.length > 0 && list.content[0] && list.content[0].id) {
                setIdMessageNew(list.content[0].id);
            }


            if (isInitialLoad) {
                setResponseMessageList(listFinal);
            } else {
                setResponseMessageList(prev => [...listFinal, ...prev]);
            }

            if (isInitialLoad) {
                setLoading(false);
            } else {
                setIsLoadingMore(false);
            }

            return listFinal.length; // Retornar la cantidad de mensajes cargados
        } catch (error) {
            console.error('Error loading messages:', error);
            setErrorMsg(error.message || "Error al cargar los mensajes");
            setLoading(false);
            setIsLoadingMore(false);
            return 0; // Retornar 0 mensajes cargados en caso de error
        }
    }, [loading, isLoadingMore, hasMoreMessages, idMessageOld, sendNotificateWS, setErrorMsg]);

    const sendMessage = async (text) => {
        if (!text.trim() || !client?.current?.id) return;

        try {
            setLoading(true);
            
            // Enviar notificación de que dejó de escribir
            sendTypingWS({
                type: 'TYPING',
                clientId: client.current.id,
                status: false,
                user: '',
                userId: 0
            });

            let contentSidTwilio = null;
            let variablesTemplateTwilio = null;

            if (selectedTemplate && selectedTemplate.type === 'WHATSAPP_TWILIO') {
                contentSidTwilio = selectedTemplate.contentSidTwilio;
                const textMessageTwilio = selectedTemplate.template;
                let jsonVariablesTwilio = generatePlaceholderJson(textMessageTwilio);
                jsonVariablesTwilio = updateJsonValues(jsonVariablesTwilio, {
                    "{1}": client.current.name,
                    "{2}": authInfo.name
                });
                variablesTemplateTwilio = JSON.stringify(jsonVariablesTwilio);
            }

            // Agregar nombre de usuario al mensaje si no está excluido
            let finalMessage = text;
            if (!includeUserName && authInfo?.name && !selectedTemplate) {
                finalMessage = `Le habla ${authInfo.name}:\n${text}`;
            }

            const messageData = {
                message: finalMessage,
                client: client.current,
                whatsappMedia: null // Campo requerido por el servicio
            };

            const response = await WhatsAppSendMessageRequest(messageData, contentSidTwilio, variablesTemplateTwilio);

            if (!response || !response.messageSid) {
                throw new Error('No se recibió respuesta del servidor');
            }

            // Agregar mensaje localmente
            const newMessage = {
                id: response.messageSid,
                message: finalMessage,
                date: new Date().toLocaleString('es', {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    second: 'numeric',
                    hour12: true
                }),
                messageIncoming: false,
                smsStatus: 'queued'
            };

            setIsNewMessage(true);
            setChatMessages(prev => [...prev, newMessage]);
            setMessage("");
            setSelectedTemplate(null); // Limpiar la plantilla seleccionada después de enviar

            // Registrar que el usuario envió un mensaje
            if (authInfo?.id && authInfo?.name) {
                logMessageSent(client.current.id, authInfo.id, authInfo.name)
                    .catch(err => console.warn('Error al registrar envío de mensaje:', err));
            }

            // Auto-pausar bot al enviar mensaje manual
            if (!client.current?.botPaused && client.current) {
                setBotPaused(true);
                const updatedClient = { ...client.current, botPaused: true };
                setClient(prev => ({ ...prev, current: updatedClient }));
                ClientToggleBotPause(updatedClient)
                    .catch(err => {
                        console.warn('Error al pausar bot:', err);
                        setBotPaused(false);
                        setClient(prev => ({ ...prev, current: { ...prev.current, botPaused: false } }));
                    });
            }

            setLoading(false);
        } catch (error) {
            console.error('Error sending message:', error);
            setErrorMsg("Error al enviar el mensaje");
            setLoading(false);
        }
    };

    const toggleBotPause = async () => {
        if (!client?.current?.id) return;

        try {
            const newPausedState = !botPaused;
            setBotPaused(newPausedState);
            const updatedClient = { ...client.current, botPaused: newPausedState };
            setClient(prev => ({ ...prev, current: updatedClient }));
            await ClientToggleBotPause(updatedClient);
        } catch (error) {
            console.error('Error al cambiar estado del bot:', error);
            setBotPaused(prev => !prev);
            setClient(prev => ({ ...prev, current: { ...prev.current, botPaused: botPaused } }));
            setErrorMsg("Error al cambiar estado del bot");
        }
    };

    const clearAiHistory = async () => {
        if (!client?.current?.cellPhone) return;
        await DeleteAiConversationHistory(client.current.cellPhone);
    };

    const updateFieldChanged = (messageSid, newValue) => {
        setChatMessages((prevItems) =>
            prevItems.map((item) =>
                item.id === messageSid ? { ...item, smsStatus: newValue } : item
            )
        );
    };

    const handleEmojiSelect = (emoji) => {
        setMessage(prev => prev + emoji);
    };

    const handleMessageChange = (event) => {
        const newValue = event.target.value;
        setMessage(newValue);
        
        if (!client?.current?.id || !authInfo?.id) return;
        
        // Enviar notificación de typing cuando el usuario está escribiendo
        if (newValue.length > 0) {
            sendTypingWS({
                type: 'TYPING',
                clientId: client.current.id,
                status: true,
                user: authInfo.name,
                userId: authInfo.id
            });
        } else {
            sendTypingWS({
                type: 'TYPING',
                clientId: client.current.id,
                status: false,
                user: '',
                userId: 0
            });
        }
    };

    const sendFile = async (file) => {
        if (!file || !client?.current?.id) return;

        try {
            setLoading(true);

            // El servicio WhatsAppSendFile agrega el timestamp al nombre internamente
            const uploadResponse = await WhatsAppSendFile(
                client.current,
                file.name,
                file
            );

            if (uploadResponse.urlStorage) {
                const messageData = {
                    message: "",
                    client: client.current,
                    whatsappMedia: {
                        mediaUrl: uploadResponse.urlStorage,
                        mediaContentType: file.type,
                        mediaName: file.name
                    }
                };

                const sendResponse = await WhatsAppSendMessageRequest(messageData);

                if (sendResponse.messageSid) {
                    const newMessage = {
                        id: sendResponse.messageSid,
                        message: "",
                        date: new Date().toLocaleString('es', {
                            day: 'numeric',
                            month: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            second: 'numeric',
                            hour12: true
                        }),
                        messageIncoming: false,
                        smsStatus: 'queued',
                        whatsappMedia: [{
                            id: 0,
                            mediaUrl: uploadResponse.urlStorage,
                            mediaContentType: file.type,
                            mediaName: file.name
                        }]
                    };

                    setIsNewMessage(true);
                    setChatMessages(prev => [...prev, newMessage]);

                    if (authInfo?.id && authInfo?.name) {
                        logMessageSent(client.current.id, authInfo.id, authInfo.name)
                            .catch(err => console.warn('Error al registrar envío de archivo:', err));
                    }
                } else {
                    // El archivo se subió pero Twilio no aceptó el mensaje
                    setErrorMsg("El archivo se cargó pero no se pudo enviar por WhatsApp");
                }
            } else {
                setErrorMsg("Error al procesar el archivo");
            }

            setLoading(false);
        } catch (error) {
            console.error('Error sending file:', error);
            setErrorMsg("Error al enviar el archivo");
            setLoading(false);
        }
    };

    /**
     * Este metodo carga los nuevos mensajes al tener una conversación activa
     */
    const loadNewMessageInWindows = (listMessages) => {
        // Actualizar status de mensajes que ya existen localmente
        listMessages.forEach((messagedb) => {
            chatMessages.forEach((messagelocal) => {
                if (messagedb.messageSid === messagelocal.id) {
                    if (messagedb.smsStatus !== messagelocal.smsStatus)
                        updateFieldChanged(messagelocal.id, messagedb.smsStatus);
                }
            });
        });

        // Usar setState funcional para deduplicar con el estado más reciente (evita closure desactualizado)
        setChatMessages((currentMessages) => {
            const toAdd = listMessages.filter(
                (messagedb) => !currentMessages.some(
                    (messagelocal) => messagelocal.id === messagedb.messageSid
                )
            );
            if (toAdd.length > 0) setIsNewMessage(true);
            return [
                ...currentMessages,
                ...toAdd.map((el) => ({
                    id: el.messageSid,
                    message: el.body,
                    date: el.dateFormat,
                    messageIncoming: el.messageIncoming,
                    smsStatus: el.smsStatus,
                    whatsappMedia: el.whatsappMedia,
                })),
            ];
        });
    };

    const loadNewMessagesIncomming = async () => {
        if(idMessageNew && idMessageNew > 0){
            const list = await notificationNewMessage(client.current.id, idMessageNew);

            sendNotificateWS([{type: 'remove', clientId:client.current.id}]);

            if (list.content.length > 0) {
                setIdMessageNew(list.content[list.content.length - 1].id);
                loadNewMessageInWindows(list.content);
            }
        }
    };
    
    const loadNewMessages = async (newMessage) => {
        if(idMessageNew && idMessageNew > 0 && newMessage?.id && newMessage.id > idMessageNew){
            let array = [];
            array.push(newMessage);
            setIdMessageNew(newMessage.id);
            loadNewMessageInWindows(array);
        }
    };

    // Manejo de notificaciones WebSocket
    useEffect(() => {
        async function fetchData() {
            if (notificationsWS?.client?.id) {
                let hasClient = false;
                if (notificationsWS.client.id === client.current.id)
                    hasClient = true;
                if (hasClient) {
                    if(notificationsWS.messageIncoming)
                        loadNewMessagesIncomming();
                    else
                        loadNewMessages(notificationsWS);
                }
            }
        }
        fetchData();
    }, [notificationsWS]);

    // Manejo de typing WebSocket
    useEffect(() => {
        setTypingClient(typingClientWS);
    }, [typingClientWS]);

    const loadTemplates = useCallback(async () => {
        try {
            // Cargar templates de Twilio
            const twilioParams = {
                criterio: null,
                type: 'WHATSAPP_TWILIO',
                active: 1,
                page: 0,
                size: 100
            };
            const twilioResponse = await getMessageTemplateRequest(twilioParams);
            if (twilioResponse && twilioResponse.content) {
                setTemplates(twilioResponse.content);
            }

            // Cargar respuestas rápidas
            const quickParams = {
                criterio: null,
                type: 'WHATSAPP_QUICK_RESPONSE',
                active: 1,
                page: 0,
                size: 100
            };
            const quickResponse = await getMessageTemplateRequest(quickParams);
            if (quickResponse && quickResponse.content) {
                setQuickResponses(quickResponse.content);
            }
        } catch (error) {
            console.error('Error loading templates:', error);
            setErrorMsg("Error al cargar las plantillas");
        }
    }, [setErrorMsg]);

    // Función para extraer las variables {1} y {2}
    const generatePlaceholderJson = (text) => {
        const matches = text.match(/\{1\}|\{2\}/g) || [];
        const result = {};

        matches.forEach((match, index) => {
            result[(index + 1).toString()] = match;
        });

        return result;
    };

    // Función para reemplazar {1} y {2} con valores personalizados
    const updateJsonValues = (json, replacements) => {
        const updatedJson = {};
        Object.keys(json).forEach((key) => {
            updatedJson[key] = replacements[json[key]] || json[key];
        });
        return updatedJson;
    };

    const handleTemplateSelect = (templateId, isQuickResponse = false) => {
        const template = isQuickResponse ?
            quickResponses.find(t => t.id === templateId) :
            templates.find(t => t.id === templateId);
        if (template) {
            let messageWithTemplate = template.template.replaceAll(
                "{1}",
                client.current.name
            );
            messageWithTemplate = messageWithTemplate.replaceAll(
                "{2}",
                authInfo.name
            );
            setMessage(messageWithTemplate);
            setSelectedTemplate(template); // Guardar la plantilla seleccionada
        }
        setShowTemplates(false);
    };

    const updateClientStage = async (newStageId, newStageName = null) => {
        if (!client?.current?.id || !newStageId) {
            setErrorMsg("Faltan datos del cliente o stage");
            return;
        }

        // Guardar el estado anterior para el log
        const previousStageId = client.current.customerStatusId;
        const previousStageName = client.current.customerStatusName || 'Sin estado';

        try {
            setLoading(true);

            // Crear el objeto de datos para actualizar el cliente
            const updatedClientData = {
                ...client.current,
                customerStatusId: newStageId
            };

            // Validar que tenemos los campos requeridos
            if (!updatedClientData.id) {
                throw new Error('ID del cliente es requerido');
            }

            // Llamar al servicio para actualizar el cliente
            await ClientUpdatedRequest(updatedClientData);

            // Crear el cliente actualizado con el nuevo stage
            const updatedClient = {
                ...client.current,
                customerStatusId: newStageId,
                customerStatusName: newStageName || client.current.customerStatusName,
                leadTagId: client.current.leadTagId,
                leadTagName: client.current.leadTagName,
                leadTagIds: client.current.leadTagIds || [],
                leadTags: client.current.leadTags || []
            };

            // Actualizar el estado local del cliente
            setClient(prev => ({
                ...prev,
                current: updatedClient
            }));

            // Actualizar también en localStorage inmediatamente
            localStorage.setItem('selectedClient', JSON.stringify(updatedClient));

            // Registrar el cambio de estado en el historial
            if (authInfo?.id && authInfo?.name && previousStageId !== newStageId) {
                logStatusChange(
                    client.current.id,
                    authInfo.id,
                    authInfo.name,
                    previousStageName,
                    newStageName || 'Nuevo estado'
                ).catch(err => console.warn('Error al registrar cambio de estado:', err));
            }

        } catch (error) {
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                status: error.response?.status,
                data: error.response?.data
            });

            // Extraer mensaje de error más específico
            let errorMessage = "Error al actualizar el stage del cliente";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message && error.message !== 'Network Error') {
                errorMessage = error.message;
            }

            setErrorMsg(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const updateClientInfo = async (updatedData) => {
        if (!client?.current?.id) {
            setErrorMsg("Faltan datos del cliente");
            return;
        }

        // Guardar los valores anteriores para el log
        const previousName = client.current.name || '';
        const previousCellPhone = client.current.cellPhone || '';
        const previousEmail = client.current.email || '';
        const previousTags = client.current.leadTags?.map(t => t.name).join(', ') || '';

        try {
            setLoading(true);

            // Crear el objeto de datos para actualizar el cliente
            const updatedClientData = {
                ...client.current,
                name: updatedData.name,
                cellPhone: updatedData.cellPhone,
                email: updatedData.email,
                leadTagId: updatedData.leadTagIds && updatedData.leadTagIds.length > 0 ? updatedData.leadTagIds[0] : null,
                leadTagName: updatedData.leadTags && updatedData.leadTags.length > 0 ? updatedData.leadTags[0].name : 'Sin asignar',
                leadTagIds: updatedData.leadTagIds,
                leadTags: updatedData.leadTags
            };

            // Llamar al servicio para actualizar el cliente
            await ClientUpdatedRequest(updatedClientData);

            // Si llegamos aquí sin excepción, asumimos que fue exitoso
            // Crear el cliente actualizado con los nuevos datos
            const updatedClient = {
                ...client.current,
                name: updatedData.name,
                cellPhone: updatedData.cellPhone,
                email: updatedData.email,
                leadTagId: updatedData.leadTagIds && updatedData.leadTagIds.length > 0 ? updatedData.leadTagIds[0] : null,
                leadTagName: updatedData.leadTags && updatedData.leadTags.length > 0 ? updatedData.leadTags[0].name : 'Sin asignar',
                leadTagIds: updatedData.leadTagIds,
                leadTags: updatedData.leadTags
            };

            // Actualizar el estado local del cliente
            setClient(prev => ({
                ...prev,
                current: updatedClient
            }));

            // Actualizar también en localStorage inmediatamente
            localStorage.setItem('selectedClient', JSON.stringify(updatedClient));

            // Registrar los cambios en el historial
            if (authInfo?.id && authInfo?.name) {
                const clientId = client.current.id;
                const userId = authInfo.id;
                const userName = authInfo.name;

                // Log cambio de nombre
                if (previousName !== updatedData.name) {
                    logClientInfoUpdate(clientId, userId, userName, 'Nombre', previousName, updatedData.name)
                        .catch(err => console.warn('Error al registrar cambio de nombre:', err));
                }

                // Log cambio de teléfono
                if (previousCellPhone !== updatedData.cellPhone) {
                    logClientInfoUpdate(clientId, userId, userName, 'Teléfono', previousCellPhone, updatedData.cellPhone)
                        .catch(err => console.warn('Error al registrar cambio de teléfono:', err));
                }

                // Log cambio de email
                if (previousEmail !== updatedData.email) {
                    logClientInfoUpdate(clientId, userId, userName, 'Email', previousEmail, updatedData.email)
                        .catch(err => console.warn('Error al registrar cambio de email:', err));
                }

                // Log cambio de etiquetas
                const newTags = updatedData.leadTags?.map(t => t.name).join(', ') || '';
                if (previousTags !== newTags) {
                    logTagChange(clientId, userId, userName, previousTags, newTags)
                        .catch(err => console.warn('Error al registrar cambio de etiquetas:', err));
                }
            }

        } catch (error) {
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                status: error.response?.status,
                data: error.response?.data
            });

            // Extraer mensaje de error más específico
            let errorMessage = "Error al actualizar la información del cliente";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message && error.message !== 'Network Error') {
                errorMessage = error.message;
            }

            setErrorMsg(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const switchToClient = useCallback(async (lead) => {
        if (!lead?.id || clientIdRef.current === lead.id) return;

        // 1. Reset all message/pagination state
        setChatMessages([]);
        setResponseMessageList([]);
        setIdMessageOld(0);
        setIdMessageNew(0);
        setHasMoreMessages(true);
        isInitialized.current = false;
        clientIdRef.current = null;

        // 2. Switch client
        setClient({ current: lead });
        localStorage.setItem('selectedClient', JSON.stringify(lead));

        // 3. Load messages with fresh pagination (idMessageOld = 0, not stale closure value)
        try {
            setLoading(true);
            isInitialized.current = true;
            clientIdRef.current = lead.id;
            setIsNewMessage(true);

            const list = await WhatsAppGetMessageListRequest(lead.id, 0, 20, 0);

            sendNotificateWS([{ type: 'remove', clientId: lead.id }]);
            // Clear unread badge immediately in all UI components (don't wait for backend re-poll)
            setMessageUser(prev => prev.filter(item => item.client?.id !== lead.id));

            if (authInfo?.id && authInfo?.name && lastViewedClientId.current !== lead.id) {
                lastViewedClientId.current = lead.id;
                logConversationViewed(lead.id, authInfo.id, authInfo.name)
                    .catch(err => console.warn('Error al registrar vista:', err));
            }

            const listFinal = list.content.reverse();
            setHasMoreMessages(listFinal.length >= 20);
            if (listFinal.length > 0 && listFinal[0]?.id) setIdMessageOld(listFinal[0].id);
            if (list.content.length > 0 && list.content[0]?.id) setIdMessageNew(list.content[0].id);

            // Set messages directly (bypasses the append-only responseMessageList effect)
            setChatMessages(listFinal.map(msg => ({
                id: msg.messageSid,
                message: msg.body,
                date: msg.dateFormat,
                messageIncoming: msg.messageIncoming,
                smsStatus: msg.smsStatus || 'sent',
                whatsappMedia: msg.whatsappMedia,
            })));

            setLoading(false);
        } catch (error) {
            console.error('Error switching client:', error);
            setErrorMsg(error.message || 'Error al cargar los mensajes');
            isInitialized.current = false;
            clientIdRef.current = null;
            setLoading(false);
        }
    }, [authInfo, sendNotificateWS, setErrorMsg, setMessageUser]);

    const deleteClient = async () => {
        if (!client?.current?.id) {
            setErrorMsg("No se puede eliminar: falta el ID del cliente");
            return false;
        }

        try {
            setLoading(true);

            // Llamar al servicio para eliminar el cliente
            await ClientDelete(client.current.id);

            // Limpiar el estado local del cliente
            setClient({ current: null });

            // Limpiar localStorage
            localStorage.removeItem('selectedClient');

            return true; // Indicar que la eliminación fue exitosa

        } catch (error) {
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                status: error.response?.status,
                data: error.response?.data
            });

            // Extraer mensaje de error más específico
            let errorMessage = "Error al eliminar el cliente";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message && error.message !== 'Network Error') {
                errorMessage = error.message;
            }

            setErrorMsg(errorMessage);
            return false; // Indicar que la eliminación falló
        } finally {
            setLoading(false);
        }
    };

    // Función para recargar la información completa del cliente desde el backend
    const reloadClientInfo = useCallback(async (clientId) => {
        try {
            const clientData = await ClientGetById(clientId);
            // Discard if the user switched to a different client while this request was in flight
            if (clientIdRef.current !== clientId) return;
            if (clientData) {
                setClient({ current: clientData });
                setBotPaused(Boolean(clientData.botPaused));
                localStorage.setItem('selectedClient', JSON.stringify(clientData));
                return clientData;
            }
        } catch (error) {
            console.error('Error al recargar información del cliente:', error);
        }
    }, []);

    useEffect(() => {
        // Siempre recargar datos frescos del cliente desde el backend
        // para evitar data stale de localStorage que cause pérdida de campos
        if (client?.current?.id) {
            reloadClientInfo(client.current.id);
        }
    }, [client?.current?.id, reloadClientInfo]);

    // Cargar templates al montar el componente
    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    // Limpiar estado al desmontar
    useEffect(() => {
        return () => {
            setChatMessages([]);
            setResponseMessageList([]);
            setMessage("");
            setLoading(false);
            setIsLoadingMore(false);
            setHasMoreMessages(true);
            setIsNewMessage(false);
            setIdMessageOld(0);
            setIdMessageNew(0);
            isInitialized.current = false; // Reset de inicialización
            clientIdRef.current = null; // Reset del cliente ID
            lastViewedClientId.current = null; // Reset para registrar vista en próxima sesión
            // No limpiamos el cliente aquí para mantenerlo en localStorage
        };
    }, []);


    // Effect para persistir el cliente en localStorage cuando cambie (solo si no viene de localStorage)
    useEffect(() => {
        if (client.current) {
            try {
                const currentStoredClient = localStorage.getItem('selectedClient');
                const storedClient = currentStoredClient ? JSON.parse(currentStoredClient) : null;

                // Solo actualizar localStorage si los datos son diferentes
                if (!storedClient || JSON.stringify(storedClient) !== JSON.stringify(client.current)) {
                    localStorage.setItem('selectedClient', JSON.stringify(client.current));
                }
            } catch (error) {
                console.error('Error updating localStorage:', error);
            }
        }
    }, [client]);

    const value = {
        chatMessages,
        message,
        setMessage,
        loading,
        isLoadingMore,
        hasMoreMessages,
        isNewMessage,
        setIsNewMessage,
        client,
        setClient,
        sendMessage,
        loadMessages,
        handleEmojiSelect,
        handleMessageChange,
        showEmojis,
        setShowEmojis,
        sendFile,
        templates,
        showTemplates,
        setShowTemplates,
        handleTemplateSelect,
        quickResponses,
        showQuickResponses,
        setShowQuickResponses,
        updateClientStage,
        updateClientInfo,
        deleteClient,
        switchToClient,
        typingClient,
        authInfo,
        includeUserName,
        setIncludeUserName,
        botPaused,
        toggleBotPause,
        clearAiHistory
    };

    return (
        <ChatNuevoContext.Provider value={value}>
            {children}
        </ChatNuevoContext.Provider>
    );
};

export default ChatNuevoProvider;
